import React, { useEffect, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { apiFetch } from '../../api/apiClient';
import { STOCK_UNIVERSE as FALLBACK_UNIVERSE } from '../../features/marketdata/stockUniverse';

/**
 * Stocks workspace layout — fetches S&P 500 universe + batch quotes once,
 * passes { universe, quotes, loading, refresh } via Outlet context to all sub-pages.
 * Auto-refreshes quotes every 5 minutes.
 */
export default function StocksWorkspace() {
  const [universe, setUniverse] = useState(FALLBACK_UNIVERSE);
  const [quotes,   setQuotes]   = useState({});
  const [loading,  setLoading]  = useState(true);

  // Fetch live S&P 500 list once on mount
  useEffect(() => {
    let cancelled = false;
    const knownWeights = new Map(FALLBACK_UNIVERSE.map(x => [x.ticker, x.weight]));
    (async () => {
      try {
        const res = await apiFetch('/api/stock/sp500');
        if (!res.ok) return;
        const list = await res.json();
        if (!cancelled && Array.isArray(list) && list.length > 0)
          setUniverse(list.map(x => ({ ...x, weight: knownWeights.get(x.ticker) || 15 })));
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchQuotes = useCallback(async () => {
    if (!universe.length) return;
    setLoading(true);
    try {
      const chunks = [];
      for (let i = 0; i < universe.length; i += 200)
        chunks.push(universe.slice(i, i + 200).map(x => x.ticker).join(','));
      const responses = await Promise.allSettled(
        chunks.map(t => apiFetch(`/api/stock/quotes?tickers=${encodeURIComponent(t)}`).then(r => r.json()))
      );
      const merged = {};
      responses.forEach(r => { if (r.status === 'fulfilled' && r.value) Object.assign(merged, r.value); });
      setQuotes(merged);
    } catch { setQuotes({}); }
    finally { setLoading(false); }
  }, [universe]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Auto-refresh every 5 min
  useEffect(() => {
    const id = setInterval(fetchQuotes, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  return <Outlet context={{ universe, quotes, loading, refresh: fetchQuotes }} />;
}
